import { useTranslation } from 'react-i18next';

import type { AssetFamily } from '~/modules/artist-roster/types';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';

import { ReleaseTag } from './ReleaseTag';
import { getContributorsByRole } from './utils';

interface TrackTableTitleCellProps {
  asset: AssetFamily;
}

export function TrackTableTitleCell({ asset }: TrackTableTitleCellProps) {
  const { t } = useTranslation('roster');

  const [isArtistCollaboratorTracksEnabled] = useFeatureFlags([
    FEATURE_FLAGS.WmgArtistCollaboratorTracks,
  ]);

  if (!isArtistCollaboratorTracksEnabled) {
    return (
      <div className="flex items-center gap-sm py-md">
        <div className="font-bold">{asset.title}</div>
        <ReleaseTag releaseDateString={asset.initialReleaseDate} />
      </div>
    );
  }

  const mainArtists = getContributorsByRole(
    asset.contributors ?? [],
    'PRIMARY_ARTIST',
  ).map((artist) => artist.name);
  const featuredArtists = getContributorsByRole(
    asset.contributors ?? [],
    'FEATURED_ARTIST',
  ).map((artist) => artist.name);

  const artistsString = featuredArtists.length
    ? t('trackList.contributors.featuring', {
        mainArtists: mainArtists.join(', '),
        featuredArtists: featuredArtists.join(', '),
      })
    : mainArtists.join(', ');

  return (
    <div className="flex max-w-[325px] flex-col gap-xs py-sm">
      <div className="flex items-center gap-sm">
        <div className="truncate text-body-md font-bold group-hover:underline">
          {asset.title}
        </div>
        <ReleaseTag releaseDateString={asset.initialReleaseDate} />
      </div>
      <div className="truncate text-caption-md font-semibold">
        {artistsString}
      </div>
    </div>
  );
}

TrackTableTitleCell.displayName = 'TrackTableTitleCell';
